import type { TouchEvent } from "react";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import FallbackImage from "../../assets/images/property-fallback-image.webp";
import OccupantsIcon from "../../assets/svg/v1/occupants-icon";
import ChevronLeftIcon from "../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../assets/svg/chevron-right-icon";
import TranslateIcon from "../../assets/svg/translate-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import ClockIcon from "../../assets/svg/clock-icon";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import { format } from "date-fns";
import { Avatar } from "../Avatar/Avatar";
import UserIcon from "../../assets/svg/user-icon";
import { Tooltip } from "../Tooltip/Tooltip";
import BulbIcon from "../../assets/svg/bulb-icon";
import FbMessengerIcon from "../../assets/svg/fb-messenger-icon";
import InstagramIcon from "../../assets/svg/instagram-icon";
import VerbasmsIcon from "../../assets/svg/verbasms-icon";
import MentionsIcon from "../../assets/svg/mentions-icon";
import {
  clientsWithFloorInfo,
  clientsWithSqftInfo,
  roomTypeLabelConfig,
  clientsWithAvailableFromOverride,
  clientsWithDateFormatDMY,
  featuresLabelConfig,
} from "../../utils/constants/propertyCardConfig";

export interface PropertyJson {
  property_id: number;
  configuration_id: number;
  room_name: string;
  property_name: string;
  amenities: string[];
  translated_amenities?: string[];
  image: string;
  source_link: string;
  lease: number;
  price: number;
  sold_out: boolean;
  occupants: number | string;
  currency: string;
  billing_unit: string;
  room_kind: string;
  installments: number;
  utility_fee: number;
  booking_link: string;
  is_booking_flow_enabled: boolean;
  cta_name?: string;
  bed_available?: number;
  available_from?: string;
  floor?: string;
  area?: string;
  sqft?: string;
  client_name?: string;
  prop_type?: string;
  furnished?: boolean | null;
}

type Modality =
  | "email"
  | "call"
  | "messenger"
  | "chat"
  | "sms"
  | "fb_messenger"
  | "instagram"
  | "mentions";

interface PropertyCardsCarouselProps {
  property_json: PropertyJson[];
  showAllTranslation: boolean;
  modality: Modality;
  iconType: string;
  showAiResponseTime: boolean;
  showSourceButton?: boolean;
  onGetSources?: (message_id: string) => void;
  isSent: boolean;
  aiResponseTime: number;
  timestamp: string;
  showTimestamp: boolean;
  participantInfo?: {
    text: string;
    fullName?: string;
    size?: number;
    showOnlineStatus?: boolean;
    isOnline?: boolean;
  };
  messageId: string;
}

const PropertyCardsCarousel: React.FC<PropertyCardsCarouselProps> = ({
  property_json,
  showAllTranslation,
  modality,
  iconType,
  showAiResponseTime,
  showSourceButton = false,
  onGetSources,
  isSent,
  aiResponseTime,
  timestamp,
  showTimestamp,
  participantInfo,
  messageId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showOriginalContent, setShowOriginalContent] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [translateX, setTranslateX] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const clientCode = useSelector(
    (state: RootState) => state?.auth.user?.client_code
  );

  const activeClientCode = useSelector(
    (state: RootState) => state?.auth.client?.client_code
  );

  const cardWidth = 312;

  useEffect(() => {
    setShowOriginalContent(!showAllTranslation);
  }, [showAllTranslation]);

  const getVisibleCards = () => {
    const cards = [];
    // If there's only one property, just return that single card
    if (property_json.length === 1) {
      cards.push({
        ...property_json[0],
        key: `${property_json[0].property_id}-0`,
        position: 0,
      });
      return cards;
    }
    // For multiple properties, show carousel with multiple cards
    for (let i = 0; i < property_json.length + 2; i++) {
      const index = (currentIndex + i) % property_json.length;
      cards.push({
        ...property_json[index],
        key: `${property_json[index].property_id}-${i}`,
        position: i,
      });
    }
    return cards;
  };

  const handleNext = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    setTranslateX((prev) => prev - cardWidth);

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % property_json.length);
      setTranslateX(0);
      setIsAnimating(false);
    }, 300);
  };

  const handlePrev = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    setTranslateX((prev) => prev + cardWidth);

    setTimeout(() => {
      setCurrentIndex((prev) =>
        prev === 0 ? property_json.length - 1 : prev - 1
      );
      setTranslateX(0);
      setIsAnimating(false);
    }, 300);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (!touchStart) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    setTouchStart(null);
  };

  const handleTranslateClick = () => {
    setShowOriginalContent(!showOriginalContent);
  };

  const getDisplayAmenities = (property: PropertyJson) => {
    if (!property) return [];

    const amenitiesSource = showOriginalContent
      ? property?.amenities
      : property?.translated_amenities;

    if (!amenitiesSource?.length && !showOriginalContent) {
      return property?.amenities || [];
    }

    if (!amenitiesSource?.length) return [];
    return amenitiesSource;
  };
  const formatDateToDMY = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-");
    if (!year || !month || !day) return dateStr;
    return `${day}/${month}/${year}`;
  };

  const getAvailableFromDisplay = (availableFrom?: string, propType?: string) => {
    if (!availableFrom) return availableFrom;

    if (
      clientsWithDateFormatDMY.includes(activeClientCode ?? "")
    ) {
      return formatDateToDMY(availableFrom);
    }

    if (
      !clientsWithAvailableFromOverride.includes(activeClientCode ?? "") ||
      propType !== "BTR"
    ) {
      return availableFrom;
    }
    const [day, month, year] = String(availableFrom).split("-").map(Number);
    if (!day || !month || !year) {
      return availableFrom;
    }
    const availableDate = new Date(year, month - 1, day);
    availableDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (availableDate > today) {
      return availableFrom;
    }
    const todayDay = String(today.getDate()).padStart(2, "0");
    const todayMonth = String(today.getMonth() + 1).padStart(2, "0");
    return `${todayDay}-${todayMonth}-${today.getFullYear()}`;
  };

  const buttonText = showOriginalContent ? "See Translation" : "Show Original";

  const modalityConfig: Record<
    Modality,
    {
      icon: React.FC<React.SVGProps<SVGSVGElement>>;
      label: string;
      color: string;
    }
  > = {
    email: {
      icon: VerbamailIcon,
      label: "VerbaMail",
      color: "text-verba-mail dark:text-verba-mail-dk",
    },
    call: {
      icon: VerbacallIcon,
      label: "VerbaCall",
      color: "text-verba-lavender dark:text-verba-lavender-dk",
    },
    messenger: {
      icon: VerbamessengerIcon,
      label: "VerbaMessenger",
      color: "text-verba-messenger dark:text-verba-messenger-dk",
    },
    chat: {
      icon: VerbachatIcon,
      label: "VerbaChat",
      color: "text-verba-chat dark:text-verba-chat-dk",
    },
    sms: {
      icon: VerbasmsIcon,
      label: "VerbaSMS",
      color: "text-verba-sms dark:text-verba-sms-dk",
    },
    fb_messenger: {
      icon: FbMessengerIcon,
      label: "Facebook Messenger",
      color: "text-verba-fb-messenger dark:text-verba-fb-messenger-dk",
    },
    instagram: {
      icon: InstagramIcon,
      label: "Instagram DMs",
      color: "",
    },
    mentions: {
      icon: MentionsIcon,
      label: "Mentions",
      color: "text-verba-indigo dark:text-verba-indigo-dk",
    },
  };

  const { icon: ModalityIcon, label, color } = modalityConfig[modality];

  const renderIcon = () => {
    if (!iconType) return null;

    return (
      <>
        {iconType === "verbaflo" && (
          <div>
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[24px] h-[24px]"
            />
          </div>
        )}
        {iconType === "participant" && participantInfo && (
          <div
            className={`flex items-center gap-2 ${
              isSent ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <div className="shrink-0 relative">
              <Avatar
                size={participantInfo.size || 24}
                text={participantInfo.text}
                className="shrink-0"
              />
              {participantInfo.showOnlineStatus && (
                <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
                  <div
                    className={`w-[4px] h-[4px] ${
                      participantInfo.isOnline
                        ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                        : "bg-offline dark:bg-offline-dk"
                    } rounded-full`}
                  ></div>
                </div>
              )}
            </div>
            {participantInfo.fullName && (
              <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                {participantInfo.fullName}
              </span>
            )}
          </div>
        )}
        {iconType === "agent" && (
          <div>
            <UserIcon className="w-[9px] h-[13px] text-verba-indigo dark:text-verba-indigo-dk" />
          </div>
        )}
      </>
    );
  };

  if (!property_json?.length) return null;

  return (
    <div
      className={`w-fit flex flex-col gap-1 mb-4.5 ${
        isSent ? "ml-auto" : "mr-auto"
      }`}
      data-property-cards-message-id={messageId}
    >
      <div
        className={`flex w-fit flex-col gap-[10px] px-[12px] py-[10px] max-w-[450px] ${
          isSent
            ? "ml-auto bg-verba-chat-bubble dark:bg-verba-chat-bubble-dk rounded-t-[8px] rounded-bl-[8px]"
            : "mr-auto bg-gray-75 dark:bg-gray-75-dk rounded-t-[8px] rounded-br-[8px]"
        } message-card`}
      >
        <p className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
          Recommended Properties
        </p>
        <div className="w-full">
          <div
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className={`flex gap-[8px] items-stretch ${
                isAnimating ? "transition-transform duration-300" : ""
              }`}
              style={{
                transform: `translateX(${translateX}px)`,
              }}
            >
              {getVisibleCards().map((property) => (
                <div key={property.key} className="shrink-0 w-[300px]">
                  <div className="border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk rounded-[8px] flex flex-col min-h-[305px] h-full">
                    <div className="h-[140px] relative rounded-t-[8px] overflow-hidden shrink-0">
                      <img
                        src={property.image || FallbackImage}
                        alt={property.property_name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-linear-to-t from-black/70 to-transparent" />

                      {property.sold_out && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                          <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[4px] py-[4px] px-[6px]">
                            Sold out
                          </span>
                        </div>
                      )}

                      {property.occupants && property.occupants !== "N/A" && (
                        <div className="absolute top-2 right-2 flex items-start gap-[2px] bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-[4px] py-[4px] px-[6px]">
                          <OccupantsIcon className="w-[12px] h-[12px] text-heading-text dark:text-heading-text-dk" />
                          <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk">
                            {property.occupants || 0} occupancy
                          </span>
                        </div>
                      )}

                      <div className="absolute bottom-4 left-2 right-2">
                        <p className="text-md font-semibold text-fixed-white dark:text-fixed-white-dk truncate">
                          {property.property_name}
                        </p>
                      </div>

                      <div className="absolute bottom-1 left-2">
                        <p className="text-xxs text-fixed-white dark:text-fixed-white-dk truncate">
                          {property.room_name}
                        </p>
                      </div>
                    </div>

                    <div className="px-[12px] py-[10px] flex flex-col flex-1 gap-[12px]">
                      <div className="flex flex-col gap-[4px] flex-1">
                        <div className="flex-col flex-wrap items-center gap-[6px]">
                          {[
                            property.room_kind && {
                              key: "room_kind",
                              label:
                                roomTypeLabelConfig[activeClientCode ?? ""] ||
                                "Room Type",
                              value: property.room_kind
                                .replace(/_/g, " ")
                                .toLowerCase(),
                            },
                            property.lease && {
                              key: "lease",
                              label: "Lease",
                              value: `${
                                property.lease
                              } ${property.billing_unit?.toLowerCase()}`,
                            },
                            property?.bed_available &&
                              property?.bed_available > 0 && {
                                key: "bed_available",
                                label: "No. of Beds",
                                value: `${property?.bed_available}`,
                              },
                            property?.available_from && {
                              key: "available_from",
                              label: "Available from",
                              value: getAvailableFromDisplay(
                                property?.available_from,
                                property?.prop_type
                              ),
                            },
                            clientsWithFloorInfo.includes(
                              activeClientCode ?? ""
                            ) &&
                              property?.prop_type === "BTR" &&
                              (property?.floor ||
                                property?.floor === "0") && {
                                key: "floor",
                                label: "Floor",
                                value:
                                  property?.floor === "0"
                                    ? "Ground"
                                    : property?.floor,
                              },
                            clientsWithSqftInfo.includes(
                              activeClientCode ?? ""
                            ) &&
                              property?.sqft && {
                                key: "area",
                                label: "Area",
                                value: `${property?.sqft} sqft`,
                              },
                            (property?.furnished === true ||
                              property?.furnished === false) && {
                              key: "furnished",
                              label: "Furnished",
                              value: property?.furnished ? "Yes" : "No",
                            },
                            property.installments && {
                              key: "installments",
                              label: "EMI",
                              value: `${property.installments} ${
                                property.installments === 1
                                  ? "installment"
                                  : "installments"
                              }`,
                            },
                            property.utility_fee && {
                              key: "utility_fee",
                              label: "Utility fee",
                              value: `${property.currency}${property.utility_fee}`,
                            },
                          ]
                            .filter(
                              (
                                field
                              ): field is {
                                key: string;
                                label: string;
                                value: string;
                              } => Boolean(field)
                            )
                            .map((field, index, array) => (
                              <div
                                key={field.key}
                                className="flex items-center"
                              >
                                <div className="flex gap-[2px] items-center pt-1">
                                  <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                                    {field.label}:
                                  </p>
                                  <p
                                    className={`text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk truncate ${
                                      field.key === "area" ? "" : "capitalize"
                                    }`}
                                  >
                                    {field.value}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                        {getDisplayAmenities(property).length > 0 && (
                          <div className="flex flex-col gap-[2px]">
                            <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                              {`${featuresLabelConfig[activeClientCode ?? ""] || "Features"}:`}
                            </p>
                            <span
                              className="line-clamp-3 text-sm text-heading-text dark:text-heading-text-dk"
                              title={getDisplayAmenities(property).join(", ")}
                            >
                              {getDisplayAmenities(property).join(", ")}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        {property.price && (
                          <div className="leading-none text-base text-verba-indigo dark:text-verba-indigo-dk font-semibold">
                            {property.currency}
                            {property.price}
                            <span className="ml-[2px] text-xxs font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                              /{property.billing_unit?.toLowerCase()}
                            </span>
                          </div>
                        )}

                        <a
                          href={
                            clientCode === "fusiongroup"
                              ? property.booking_link
                              : property.source_link
                          }
                          className="bg-verba-blue dark:bg-verba-blue-dk border border-verba-blue dark:border-verba-blue-dk text-fixed-white dark:text-fixed-white-dk text-xs font-semibold rounded-[4px] py-[2.5px] px-[11px]"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {property?.cta_name || "Visit Property"}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mt-3">
            {(property_json?.[0]?.translated_amenities?.length ?? 0) > 0 && (
              <button
                className="w-max flex items-center gap-[4px] text-md font-semibold text-verba-blue-dark-mode dark:text-verba-blue-dark-mode-dk hover:underline"
                onClick={handleTranslateClick}
              >
                <TranslateIcon className="w-[14px] h-[14px]" />
                <p>{buttonText}</p>
              </button>
            )}

            {property_json.length > 1 ? (
              <div className="flex items-center gap-3 ml-auto">
                <div className="text-sm text-dark-gray dark:text-dark-gray-dk font-medium">
                  {currentIndex + 1} / {property_json.length}
                </div>

                <div className="flex items-center gap-[6px]">
                  <button
                    onClick={handlePrev}
                    className="w-[26px] h-[26px] bg-popper-bg dark:bg-popper-bg-dark border border-border-gray dark:border-border-gray-dark rounded-full flex items-center justify-center"
                  >
                    <ChevronLeftIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dark" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="w-[26px] h-[26px] bg-popper-bg dark:bg-popper-bg-dark border border-border-gray dark:border-border-gray-dark rounded-full flex items-center justify-center"
                  >
                    <ChevronRightIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dark" />
                  </button>
                </div>
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
      <div
        className={`flex items-center justify-end gap-[4px] ${
          isSent ? "flex-row" : "flex-row-reverse"
        }`}
      >
        <div className="flex items-center gap-[2px]">
          <ModalityIcon className={`w-[14px] h-[14px] ${color}`} />
          {isSent && (
            <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {label}
            </p>
          )}
        </div>

        {showAiResponseTime && aiResponseTime && aiResponseTime > 0 && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <div className="flex items-center gap-[2px]">
              <ClockIcon className="w-[11px] h-[11px] text-gray-placeholder dark:text-gray-placeholder-dk" />
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk leading-none">
                {aiResponseTime?.toFixed(1)}s
              </p>
            </div>
          </>
        )}

        {showTimestamp && timestamp && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                {format(new Date(timestamp || ""), "hh:mm a")}
              </p>
            </div>
          </>
        )}

        {showSourceButton && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <Tooltip content="View Source">
              <button
                className="hover:text-verba-blue dark:hover:text-verba-blue-dk transition-colors  flex items-center justify-center"
                onClick={() => onGetSources && onGetSources?.(messageId)}
              >
                <BulbIcon className="w-[18px] h-[18px] text-gray-placeholder dark:text-gray-placeholder-dk mb-1" />
              </button>
            </Tooltip>
          </>
        )}

        {iconType && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            {renderIcon()}
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyCardsCarousel;
